import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import BulkApproveModal from '../components/BulkApproveModal';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Priority-based categories: Green (safest) → Red (needs attention)
// Key = trend_type + "_" + consistency_level
const CATEGORY_CONFIG = {
  // 🟢 GREEN - Safest, auto-approve candidates
  'on_track_high': { 
    label: '✅ Perfect Progress', 
    shortLabel: 'Perfect',
    priority: 1,
    color: 'bg-green-500',
    badgeColor: 'bg-green-100 text-green-800 border-green-300',
    headerColor: 'bg-green-50 border-l-4 border-l-green-500',
    description: 'High adherence, losing at healthy rate'
  },
  'on_track_medium': { 
    label: '🟢 Good Progress', 
    shortLabel: 'Good',
    priority: 2,
    color: 'bg-green-400',
    badgeColor: 'bg-green-100 text-green-700 border-green-200',
    headerColor: 'bg-green-50/70 border-l-4 border-l-green-400',
    description: 'On track but could improve adherence'
  },
  'slow_high': { 
    label: '🟢 Steady Progress', 
    shortLabel: 'Steady',
    priority: 3,
    color: 'bg-lime-500',
    badgeColor: 'bg-lime-100 text-lime-800 border-lime-300',
    headerColor: 'bg-lime-50 border-l-4 border-l-lime-500',
    description: 'High adherence, slower but consistent'
  },
  
  // 🟡 YELLOW - Needs minor adjustment
  'plateau_high': { 
    label: '🟡 Plateau', 
    shortLabel: 'Plateau',
    priority: 4,
    color: 'bg-yellow-500',
    badgeColor: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    headerColor: 'bg-yellow-50 border-l-4 border-l-yellow-500',
    description: 'High adherence but weight stuck - needs calorie cut'
  },
  'slow_medium': { 
    label: '🟡 Slow + Inconsistent', 
    shortLabel: 'Slow',
    priority: 5,
    color: 'bg-yellow-400',
    badgeColor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    headerColor: 'bg-yellow-50/70 border-l-4 border-l-yellow-400',
    description: 'Slow progress with medium adherence'
  },
  'plateau_medium': { 
    label: '🟡 Plateau + Inconsistent', 
    shortLabel: 'Plateau',
    priority: 6,
    color: 'bg-amber-500',
    badgeColor: 'bg-amber-100 text-amber-800 border-amber-300',
    headerColor: 'bg-amber-50 border-l-4 border-l-amber-500',
    description: 'Plateau with medium adherence'
  },

  // 🟠 ORANGE - Needs attention
  'too_fast_high': { 
    label: '🟠 Losing Too Fast', 
    shortLabel: 'Too Fast',
    priority: 7,
    color: 'bg-orange-500',
    badgeColor: 'bg-orange-100 text-orange-800 border-orange-300',
    headerColor: 'bg-orange-50 border-l-4 border-l-orange-500',
    description: 'High adherence but losing too quickly - increase calories'
  },
  'too_fast_medium': { 
    label: '🟠 Fast + Inconsistent', 
    shortLabel: 'Too Fast',
    priority: 8,
    color: 'bg-orange-400',
    badgeColor: 'bg-orange-100 text-orange-700 border-orange-200',
    headerColor: 'bg-orange-50/70 border-l-4 border-l-orange-400',
    description: 'Losing too fast with medium adherence'
  },
  'going_up_high': { 
    label: '🟠 Gaining Weight', 
    shortLabel: 'Gaining',
    priority: 9,
    color: 'bg-orange-600',
    badgeColor: 'bg-orange-100 text-orange-800 border-orange-400',
    headerColor: 'bg-orange-50 border-l-4 border-l-orange-600',
    description: 'High adherence but gaining - review plan'
  },
  'going_up_medium': { 
    label: '🟠 Gaining + Inconsistent', 
    shortLabel: 'Gaining',
    priority: 10,
    color: 'bg-orange-500',
    badgeColor: 'bg-orange-100 text-orange-700 border-orange-300',
    headerColor: 'bg-orange-50/70 border-l-4 border-l-orange-500',
    description: 'Gaining with medium adherence'
  },

  // 🔴 RED - Low adherence - focus on building habits
  'on_track_low': { 
    label: '🔴 Build Consistency', 
    shortLabel: 'Habits',
    priority: 11,
    color: 'bg-red-400',
    badgeColor: 'bg-red-100 text-red-700 border-red-200',
    headerColor: 'bg-red-50/70 border-l-4 border-l-red-400',
    description: 'Low adherence - encourage to focus on 1 meal at a time'
  },
  'plateau_low': { 
    label: '🔴 Build Consistency', 
    shortLabel: 'Habits',
    priority: 12,
    color: 'bg-red-500',
    badgeColor: 'bg-red-100 text-red-800 border-red-300',
    headerColor: 'bg-red-50 border-l-4 border-l-red-500',
    description: 'Low adherence - focus on building habits before adjusting calories'
  },
  'slow_low': { 
    label: '🔴 Build Consistency', 
    shortLabel: 'Habits',
    priority: 12,
    color: 'bg-red-500',
    badgeColor: 'bg-red-100 text-red-800 border-red-300',
    headerColor: 'bg-red-50 border-l-4 border-l-red-500',
    description: 'Low adherence - encourage small wins, 1 meal at a time'
  },
  'too_fast_low': { 
    label: '🔴 Inconsistent Progress', 
    shortLabel: 'Inconsistent',
    priority: 13,
    color: 'bg-red-500',
    badgeColor: 'bg-red-100 text-red-800 border-red-300',
    headerColor: 'bg-red-50 border-l-4 border-l-red-500',
    description: 'Losing but inconsistent - focus on sustainable habits'
  },
  'going_up_low': { 
    label: '🔴 Needs Support', 
    shortLabel: 'Support',
    priority: 14,
    color: 'bg-red-600',
    badgeColor: 'bg-red-100 text-red-800 border-red-400',
    headerColor: 'bg-red-50 border-l-4 border-l-red-600',
    description: 'Struggling - check in personally, identify barriers'
  },
};

// 📋 EARLY WEEKS & NEW CHECK-INS - No action needed (gathering data)
const EARLY_WEEK_CATEGORIES = {
  'week_1_baseline': {
    label: '📋 Week 1: Baseline',
    shortLabel: 'Week 1',
    priority: 0,
    color: 'bg-blue-400',
    badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
    headerColor: 'bg-blue-50/70 border-l-4 border-l-blue-400',
    description: 'Gathering initial data. No changes needed yet.',
    noAction: true
  },
  'week_2_monitoring': {
    label: '📋 Week 2: Monitoring',
    shortLabel: 'Week 2',
    priority: 0,
    color: 'bg-blue-500',
    badgeColor: 'bg-blue-100 text-blue-800 border-blue-300',
    headerColor: 'bg-blue-50 border-l-4 border-l-blue-500',
    description: 'Monitoring trends. Early changes are often water weight.',
    noAction: true
  },
  'extreme_weight_change': {
    label: '⚠️ Large Scale Change',
    shortLabel: 'Review',
    priority: 0,
    color: 'bg-purple-500',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-300',
    headerColor: 'bg-purple-50 border-l-4 border-l-purple-500',
    description: 'Large weight fluctuation. Coach review required.',
    noAction: true
  },
  'pending_review': {
    label: '📋 New Check-in',
    shortLabel: 'New',
    priority: 0,
    color: 'bg-blue-500',
    badgeColor: 'bg-blue-100 text-blue-700 border-blue-200',
    headerColor: 'bg-blue-50 border-l-4 border-l-blue-500',
    description: 'New check-in awaiting processing. Click "Generate Adjustments" to categorize.',
    noAction: false  // Allow approval for new check-ins
  }
};

// Fallback for unknown combinations
const DEFAULT_CATEGORY = {
  label: '⚪ Unknown',
  shortLabel: 'Unknown',
  priority: 99,
  color: 'bg-gray-400',
  badgeColor: 'bg-gray-100 text-gray-700 border-gray-300',
  headerColor: 'bg-gray-50 border-l-4 border-l-gray-400',
  description: 'Uncategorized'
};

// Filter tabs - grouped by color
const FILTER_TABS = [
  { key: 'all', label: 'All', color: 'bg-gray-900' },
  { key: 'new', label: '📋 New Clients', color: 'bg-blue-500', trends: ['week_1_baseline', 'week_2_monitoring', 'extreme_weight_change', 'pending_review'] },
  { key: 'green', label: '🟢 Safe', color: 'bg-green-500', trends: ['on_track_high', 'on_track_medium', 'slow_high'] },
  { key: 'yellow', label: '🟡 Adjust', color: 'bg-yellow-500', trends: ['plateau_high', 'slow_medium', 'plateau_medium'] },
  { key: 'orange', label: '🟠 Attention', color: 'bg-orange-500', trends: ['too_fast_high', 'too_fast_medium', 'going_up_high', 'going_up_medium'] },
  { key: 'red', label: '🔴 Review', color: 'bg-red-500', trends: ['on_track_low', 'plateau_low', 'slow_low', 'too_fast_low', 'going_up_low'] },
];

export default function PendingAdjustments({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncingGoals, setSyncingGoals] = useState(false);
  const [bulkApproveModal, setBulkApproveModal] = useState({ open: false, scope: 'all', bucket: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetchPendingAdjustments();
  }, []);

  const fetchPendingAdjustments = async () => {
    try {
      const response = await axios.get(`${API}/coach/pending-adjustments`);
      setData(response.data);
      setExpandedGroups(new Set());
    } catch (error) {
      toast.error('Failed to load pending adjustments');
    } finally {
      setLoading(false);
    }
  };

  // Get category key for a client
  const getCategoryKey = (client) => {
    // Check for early week status first (Week 1, Week 2)
    if (client.early_week_status) {
      return client.early_week_status;
    }
    // Check for extreme weight change
    if (client.extreme_weight_change) {
      return 'extreme_weight_change';
    }
    // For clients with check-in data but no trend analysis yet
    if (client.checkin_data && !client.trend_type) {
      return 'pending_review';
    }
    return `${client.trend_type}_${client.consistency_level}`;
  };

  // Get category config for a client
  const getCategory = (client) => {
    const key = getCategoryKey(client);
    
    // Check early week categories first (includes pending_review)
    if (EARLY_WEEK_CATEGORIES[key]) {
      return EARLY_WEEK_CATEGORIES[key];
    }
    
    return CATEGORY_CONFIG[key] || DEFAULT_CATEGORY;
  };
  
  // Check if a client needs action (not early week or extreme change)
  const clientNeedsAction = (client) => {
    return client.adjustment_allowed !== false && 
           !client.early_week_status && 
           !client.extreme_weight_change;
  };

  // Calculate stats by color group
  const stats = useMemo(() => {
    if (!data?.groups) return { all: 0, new: 0, green: 0, yellow: 0, orange: 0, red: 0 };
    
    const counts = { all: 0, new: 0, green: 0, yellow: 0, orange: 0, red: 0 };
    
    data.groups.forEach(group => {
      group.clients.forEach(client => {
        const key = getCategoryKey(client);
        counts.all++;
        
        // Determine color group - check new clients first
        if (FILTER_TABS[1].trends.includes(key)) counts.new++;
        else if (FILTER_TABS[2].trends.includes(key)) counts.green++;
        else if (FILTER_TABS[3].trends.includes(key)) counts.yellow++;
        else if (FILTER_TABS[4].trends.includes(key)) counts.orange++;
        else if (FILTER_TABS[5].trends.includes(key)) counts.red++;
      });
    });
    
    return counts;
  }, [data]);

  // Reorganize data by priority category instead of template
  const categorizedClients = useMemo(() => {
    if (!data?.groups) return [];
    
    // Flatten all clients and add their category
    const allClients = [];
    data.groups.forEach(group => {
      group.clients.forEach(client => {
        const category = getCategory(client);
        allClients.push({
          ...client,
          categoryKey: getCategoryKey(client),
          category,
          template: group.template
        });
      });
    });

    // Filter by color group
    let filtered = allClients;
    if (activeFilter !== 'all') {
      const filterTab = FILTER_TABS.find(t => t.key === activeFilter);
      if (filterTab?.trends) {
        filtered = allClients.filter(c => filterTab.trends.includes(c.categoryKey));
      }
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      );
    }

    // Group by category and sort by priority
    const grouped = {};
    filtered.forEach(client => {
      const key = client.categoryKey;
      if (!grouped[key]) {
        grouped[key] = {
          categoryKey: key,
          category: client.category,
          clients: []
        };
      }
      grouped[key].clients.push(client);
    });

    // Sort groups by priority
    return Object.values(grouped).sort((a, b) => a.category.priority - b.category.priority);
  }, [data, activeFilter, searchQuery]);

  // Count only actionable clients (not early weeks or extreme changes)
  const totalFiltered = useMemo(() => {
    return categorizedClients.reduce((sum, g) => sum + g.clients.length, 0);
  }, [categorizedClients]);
  
  const totalActionable = useMemo(() => {
    return categorizedClients.reduce((sum, g) => 
      sum + g.clients.filter(c => clientNeedsAction(c)).length, 0);
  }, [categorizedClients]);

  const toggleGroup = (key) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    setExpandedGroups(new Set(categorizedClients.map(g => g.categoryKey)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleBulkApprove = (scope = 'all', bucket = null) => {
    setBulkApproveModal({ open: true, scope, bucket });
  };

  const handleBulkApproveComplete = (result) => {
    toast.success(`Approved ${result.approved_count} adjustments! ${result.skipped_exception_count > 0 ? `(${result.skipped_exception_count} exceptions skipped)` : ''}`);
    fetchPendingAdjustments();
  };

  const handleBulkApproveClose = () => {
    setBulkApproveModal({ open: false, scope: 'all', bucket: null });
  };

  const handleApproveOne = async (adjustmentId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/coach/approve-adjustment/${adjustmentId}`);
      toast.success('Adjustment approved!');
      fetchPendingAdjustments();
    } catch (error) {
      toast.error('Failed to approve adjustment');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectOne = async (adjustmentId) => {
    if (!window.confirm('Reject this adjustment and keep current plan?')) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/coach/reject-adjustment/${adjustmentId}`);
      toast.success('Adjustment rejected');
      fetchPendingAdjustments();
    } catch (error) {
      toast.error('Failed to reject adjustment');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateAdjustments = async () => {
    if (!window.confirm('Generate weekly adjustments for all clients with check-ins?')) return;
    
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/generate-weekly-adjustments`);
      const results = response.data;
      toast.success(`Generated ${results.generated} adjustment${results.generated !== 1 ? 's' : ''}!`);
      await fetchPendingAdjustments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate adjustments');
    } finally {
      setGenerating(false);
    }
  };

  const handleSyncGoalTypes = async () => {
    setSyncingGoals(true);
    try {
      const response = await axios.post(`${API}/admin/backfill-client-goal-types`);
      const results = response.data;
      if (results.updated > 0) {
        toast.success(`Synced goal types for ${results.updated} client${results.updated !== 1 ? 's' : ''}!`);
      } else if (results.skipped_already_set > 0) {
        toast.info('All clients already have goal types set');
      } else {
        toast.info('No clients needed updating');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync goal types');
    } finally {
      setSyncingGoals(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <Card><CardContent className="py-12 text-center">Loading...</CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Weekly Reviews</h1>
            <p className="text-gray-600 mt-1">
              {data?.pending_count || 0} client{data?.pending_count !== 1 ? 's' : ''} pending review
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSyncGoalTypes}
              disabled={syncingGoals}
              variant="outline"
              className="border-gray-300"
              title="Sync goal types (Weight Loss/Gain/Maintain) for all clients from their onboarding data"
            >
              {syncingGoals ? 'Syncing...' : '🎯 Sync Goals'}
            </Button>
            <Button
              onClick={handleGenerateAdjustments}
              disabled={generating}
              className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
            >
              {generating ? 'Generating...' : '🔄 Generate Adjustments'}
            </Button>
          </div>
        </div>

        {/* Color Filter Tabs */}
        {data?.pending_count > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${
                  activeFilter === tab.key 
                    ? `${tab.color} text-white shadow-md` 
                    : 'bg-white text-gray-700 border hover:bg-gray-50'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeFilter === tab.key ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {stats[tab.key] || 0}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Search and Controls */}
        {data?.pending_count > 0 && (
          <div className="mb-6 flex gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>
                Expand All
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                Collapse All
              </Button>
            </div>
          </div>
        )}

        {/* Bulk Approve Banner - only show if there are actionable clients */}
        {totalActionable > 0 && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-800">Quick Approval</h3>
                  <p className="text-sm text-green-600">
                    Preview and approve {totalActionable} safe adjustment{totalActionable !== 1 ? 's' : ''} at once
                    {totalFiltered > totalActionable && (
                      <span className="text-gray-500 ml-1">
                        ({totalFiltered - totalActionable} early week{totalFiltered - totalActionable !== 1 ? 's' : ''} excluded)
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => handleBulkApprove('all')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve All Safe
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Info banner when there are only non-actionable clients */}
        {totalFiltered > 0 && totalActionable === 0 && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-lg">📋</span>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800">Gathering Data</h3>
                  <p className="text-sm text-blue-600">
                    {totalFiltered} client{totalFiltered !== 1 ? 's' : ''} in early weeks or flagged for review. No adjustments needed yet.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {(!data || data.pending_count === 0) && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <p className="text-lg">No pending adjustments</p>
              <p className="text-sm mt-2">Click &quot;Generate Adjustments&quot; to create adjustments for clients with check-ins</p>
            </CardContent>
          </Card>
        )}

        {/* No Results from Filter */}
        {data?.pending_count > 0 && totalFiltered === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <p className="text-lg">No matching clients</p>
              <p className="text-sm mt-2">Try adjusting your filter or search</p>
              <Button variant="outline" className="mt-4" onClick={() => { setActiveFilter('all'); setSearchQuery(''); }}>
                Clear Filters
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Categorized Groups */}
        <div className="space-y-4">
          {categorizedClients.map((group) => {
            const isExpanded = expandedGroups.has(group.categoryKey);
            const category = group.category;
            
            return (
              <Card key={group.categoryKey} className={`overflow-hidden ${category.headerColor}`}>
                {/* Collapsible Header */}
                <div 
                  className="px-6 py-4 cursor-pointer hover:bg-white/50 transition flex justify-between items-center"
                  onClick={() => toggleGroup(group.categoryKey)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                    <div>
                      <h3 className="font-bold text-gray-900 flex items-center gap-3">
                        {category.label}
                        <Badge className="bg-white text-gray-700 border text-xs">
                          {group.clients.length} client{group.clients.length !== 1 ? 's' : ''}
                        </Badge>
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">{category.description}</p>
                    </div>
                  </div>
                  {/* Only show Approve All for actionable groups */}
                  {group.clients.some(c => clientNeedsAction(c)) ? (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Only approve clients that need action
                        group.clients.filter(c => clientNeedsAction(c)).forEach(c => handleApproveOne(c.adjustment_id));
                      }}
                      disabled={processing}
                      className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve All ({group.clients.filter(c => clientNeedsAction(c)).length})
                    </Button>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-3 py-1">
                      No Action Needed
                    </Badge>
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <CardContent className="pt-0 bg-white border-t">
                    <div className="divide-y">
                      {group.clients.map((client, idx) => {
                        const isEarlyWeek = client.early_week_status || client.extreme_weight_change;
                        const needsAction = clientNeedsAction(client);
                        
                        return (
                        <div key={idx} className="py-4 flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">{client.name}</span>
                              {/* Show early week badge */}
                              {client.early_week_status === 'week_1_baseline' && (
                                <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                                  Week 1
                                </Badge>
                              )}
                              {client.early_week_status === 'week_2_monitoring' && (
                                <Badge className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                                  Week 2
                                </Badge>
                              )}
                              {client.extreme_weight_change && (
                                <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-200">
                                  ⚠️ {client.weight_delta_1w > 0 ? '+' : ''}{client.weight_delta_1w?.toFixed(1)} kg this week
                                </Badge>
                              )}
                              {!isEarlyWeek && client.checkin_data && (
                                <Badge className="text-xs bg-blue-100 text-blue-700">
                                  New Check-in
                                </Badge>
                              )}
                              {client.consistency_score != null && (
                                <Badge className={`text-xs ${
                                  client.consistency_level === 'high' ? 'bg-blue-100 text-blue-700' :
                                  client.consistency_level === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {client.consistency_score}/5 adherence
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{client.email}</p>
                            
                            {/* Show early week message */}
                            {client.early_week_message && (
                              <p className="mt-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                                {client.early_week_message}
                              </p>
                            )}
                            
                            <div className="mt-2 flex flex-wrap gap-4 text-sm">
                              {/* Show check-in data if available */}
                              {client.checkin_data && (
                                <>
                                  <span>
                                    <span className="text-gray-500">Weight:</span>
                                    <span className="ml-1 font-medium">{client.checkin_data.weight} kg</span>
                                  </span>
                                  <span>
                                    <span className="text-gray-500">Consistency:</span>
                                    <span className="ml-1 font-medium">{client.checkin_data.consistency}/5</span>
                                  </span>
                                  <span>
                                    <span className="text-gray-500">Energy:</span>
                                    <span className="ml-1 font-medium">{client.checkin_data.energy}/10</span>
                                  </span>
                                </>
                              )}
                              {/* Show calorie info ONLY if NOT early week - no calorie changes for early weeks */}
                              {!isEarlyWeek && client.old_calories != null && (
                                <span>
                                  <span className="text-gray-500">Current:</span>
                                  <span className="ml-1 font-medium">{Math.round(client.old_calories)} kcal</span>
                                </span>
                              )}
                              {!isEarlyWeek && client.new_calories != null && client.new_calories !== client.old_calories && (
                                <span>
                                  <span className="text-gray-500">→</span>
                                  <span className={`ml-1 font-bold ${
                                    client.new_calories > client.old_calories ? 'text-green-600' :
                                    client.new_calories < client.old_calories ? 'text-orange-600' :
                                    'text-gray-600'
                                  }`}>
                                    {Math.round(client.new_calories)} kcal
                                  </span>
                                </span>
                              )}
                              {client.weight_delta_2w != null && (
                                <span className={
                                  client.weight_delta_2w < -0.5 ? 'text-green-600' : 
                                  client.weight_delta_2w > 0.5 ? 'text-red-600' : 
                                  'text-gray-500'
                                }>
                                  {client.weight_delta_2w > 0 ? '+' : ''}{client.weight_delta_2w.toFixed(1)} kg/2wk
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            {needsAction ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => handleApproveOne(client.adjustment_id)}
                                  disabled={processing}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRejectOne(client.adjustment_id)}
                                  disabled={processing}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600 border-gray-200 px-3 py-1">
                                No Action Needed
                              </Badge>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* Bulk Approve Modal */}
      <BulkApproveModal
        open={bulkApproveModal.open}
        onClose={handleBulkApproveClose}
        onComplete={handleBulkApproveComplete}
        scope={bulkApproveModal.scope}
        bucket={bulkApproveModal.bucket}
      />
    </div>
  );
}
